import type { WithId } from '@medplum/core';
import type { CarePlan } from '@medplum/fhirtypes';
import { useEffect, useState } from 'react';

import { useMedplum } from '../medplum/MedplumProvider';

import { fetchPatientCarePlans } from './carePlanFhir';

interface PatientCarePlansResult {
  carePlans: WithId<CarePlan>[];
  isLoading: boolean;
  isError: boolean;
}

/**
 * Loads a patient's CarePlans from Medplum FHIR. `fhirPatientId` is the FHIR
 * Patient resource id (resolve it from the Eluve patient id via
 * `useMedplumPatientAccess`). Returns an empty list while the id or client is
 * unavailable, and on error.
 */
export const usePatientCarePlans = (
  fhirPatientId: string | null,
): PatientCarePlansResult => {
  const { client } = useMedplum();
  const [carePlans, setCarePlans] = useState<WithId<CarePlan>[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    if (!client || !fhirPatientId) {
      setCarePlans([]);
      setIsLoading(false);
      setIsError(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setIsError(false);

    fetchPatientCarePlans(client, fhirPatientId)
      .then((plans) => {
        if (!cancelled) setCarePlans(plans);
      })
      .catch(() => {
        if (cancelled) return;
        setCarePlans([]);
        setIsError(true);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [client, fhirPatientId]);

  return { carePlans, isLoading, isError };
};
